import {observer} from 'mobx-react';
import moment from 'moment';
import {useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import Badge from '../../../components/bootstrap/Badge';
import Card, {CardActions, CardBody, CardFooter, CardFooterRight, CardHeader, CardLabel, CardTitle} from '../../../components/bootstrap/Card';
import Spinner from '../../../components/bootstrap/Spinner';
import Icon from '../../../components/icon/Icon';
import useRequest from '../../../hooks/useRequest';
import {useMst} from '../../../models';
import CompanyService from '../../../services/CompanyService';
import {useTranslation} from 'react-i18next';
import showNotification from '../../../components/extras/showNotification';
import {adminsMenu} from '../../../menu';
import {convertMinsToHrsMins_v2} from "../../../lib/Util";
import Input from 'components/bootstrap/forms/Input';
import Button from 'components/bootstrap/Button';
import ButtonBasic from 'components/bootstrap/ButtonBasic';
import {TimeBar} from 'pages/AuthMenu/Work/WorkingFlex/components/charts';
import Pagination, {PaginationItem} from 'components/bootstrap/Pagination';
import imgNoData from "../../../assets/img/deadline/img_no_data.png"
import { DateRange } from 'react-date-range';
import * as locales from 'react-date-range/dist/locale';
import ThemeContext from 'contexts/themeContext';
import { useEffectOnce } from 'react-use';
import { CalendarBox, CalendarPresetFilter } from 'components/common/CalendarBox';
import { getPresetRange } from 'helpers/dateRange';
import { localizedLabel } from 'helpers/localizedField';
import { getPersonDisplayName } from 'helpers/personNameI18n';

const OvertimeWorkers = observer(({ workTime = 40, workOverTime = 12 }) => {
	const { company } = useMst();
	const { t } = useTranslation();
	const tr = useCallback((name) => t(`userDashboard.${name}`), [t]);
	const tr2 = useCallback((name) => t(`restManage.${name}`), [t]);
	const { responseData, loading, request } = useRequest([]);
	const sortedData = useMemo(() => [...responseData].sort((a, b) => a.workHourRank - b.workHourRank), [responseData]); // 순위로 오름차순 정렬
	const navigate = useNavigate();
	const [commCodeList, setCommCodeList] = useState([]); // 공통코드 데이터

	/////// 달력 관련 --s
	const { lang } = useContext(ThemeContext);
	const dateFilterPopRef = useRef({});
	const popRef = useRef({});
	const [isDisabled, setIsDisabled] = useState(false);
	// 달력 필터 탭 상태
	const [selectedButton, setSelectedButton] = useState({
		index: 0,
		status: {
			value: 'week',
			label: t('common.label.weekly'),
		},
	});
	// 달력 팝오버에서 조회를 누르기 전 상태 날짜
	const [tempSelectionRange, setTempSelectionRange] = useState([
		{
			startDate: new Date(), // addDays(new Date(), -7)
			endDate: new Date(),
			key: 'selection',
		},
	]);
	// 화면에 보여질 확정된 날짜
	const [selectionRange, setSelectionRange] = useState({
		startDate: moment().startOf('isoWeek').toDate(),
		endDate: moment().endOf('isoWeek').toDate(),
		key: 'selection',
	});

	// 달력 필터
	const [dateFilterOpen, setDateFilterOpen] = useState(false);
	const [yearFilter, setYearFilter] = useState([]);

	// 달력 아이콘 년도별 목록 (회사 가입일 연도 ~ 현재 연도)
	const getYearFilter = async () => {
		let YearData = [];
		let i = 0;
		while (parseInt(moment(company?.get?.companyAnniversary).format('YYYY')) + i <= parseInt(moment().format('YYYY'))) {
			YearData.push(parseInt(moment(company?.get?.companyAnniversary).format('YYYY')) + i);
			i++;
		}
		setYearFilter(YearData);
	};

	useEffectOnce(() => {
		getYearFilter();
	}, []);

	// 달력 아이콘 필터 적용 (주별/월별/분기별/반기별/년도별)
	const filterApply = ({ filterKeyword, filterValue, filterText }) => {
		if (filterKeyword !== '날짜') return;

		const range = getPresetRange(filterValue, filterText, selectionRange?.startDate);
		if (range) {
			setSelectionRange({ key: 'selection', startDate: range.startDate, endDate: range.endDate });
		}
	};

	// 이전 버튼 클릭 시
	const handlePrevClick = () => {
		const filter = selectedButton.status.value; // 필터 값
		const filterCalc = (num, filter, x) => {
			const prevStartDate = moment(selectionRange?.startDate).subtract(num, filter).startOf(x).toDate();
			const prevEndDate = moment(selectionRange?.endDate).subtract(num, filter).endOf(x).toDate();

			setSelectionRange({ key: 'selection', startDate: prevStartDate, endDate: prevEndDate });
		}

		if(filter === 'month'){
			filterCalc(1, 'month', 'month')
		}else if(filter === 'quarter'){
			filterCalc(1, 'quarter', 'quarter')
		}else if(filter === 'half'){
			filterCalc(6, 'month', 'month')
		}else if(filter === 'year'){
			filterCalc(1, 'year', 'year')
		}else{
			filterCalc(1, 'week', 'isoWeek')
		}
	};

	// 다음 버튼 클릭 시
	const handleNextClick = () => {
		const filter = selectedButton.status.value; // 필터 값
		const filterCalc = (num, filter, x) => {
			const nextStartDate = moment(selectionRange?.startDate).add(num, filter).startOf(x).toDate();
			const nextEndDate = moment(selectionRange?.endDate).add(num, filter).endOf(x).toDate();

			setSelectionRange({ key: 'selection', startDate: nextStartDate, endDate: nextEndDate });
		}

		if(filter === 'month'){
			filterCalc(1, 'month', 'month')
		}else if(filter === 'quarter'){
			filterCalc(1, 'quarter', 'quarter')
		}else if(filter === 'half'){
			filterCalc(6, 'month', 'month')
		}else if(filter === 'year'){
			filterCalc(1, 'year', 'year')
		}else{
			filterCalc(1, 'week', 'isoWeek')
		}
	};

	// Popovers에서 조회 버튼 클릭 시
	const handlePopoversSearchClick = () => {
		const startDate = tempSelectionRange[0].startDate;
		const endDate = tempSelectionRange[0].endDate;
		setSelectionRange({ key: 'selection', startDate, endDate });
		if (popRef.current) popRef.current.onOpen();
	};
	/////// 달력 관련 --e

	// 공통코드 데이터 가져오기
	const getCommCodes = async () => {
		await CompanyService.getCommCodeNew({
			companyId: company?.get?.id,
			commCodeType: 'WEEKLY_WORK_STS_RANGE'
		}).then(res => {
			setCommCodeList(
				res?.data?.map(v => {
					return { value: String(v?.detail_seq), label: localizedLabel(t, v, 'comm_code_name'), over: v?.etc1, under: v?.etc2, comm_code:v?.comm_code };
				}) || []
			);
		});
	}

	useEffect(() => {
		getCommCodes();
	}, []);

	// 공통코드 데이터 (주의, 위험 값)
	const dangerCode = commCodeList.find(v => v.comm_code === 'DANGER'); // 위험
	const warnCode = commCodeList.find(v => v.comm_code === 'WARN'); // 주의

	// 페이지네이션
	const [perPage, setPerPage] = useState(6); // 보여줄 리스트 개수
	const totalPage = Math.ceil(sortedData.length / perPage);
	const [activePage, setActivePage] = useState(1);
	const pageGroupSize = 5; // 보여줄 페이지네이션 개수
	const currentGroup = Math.ceil(activePage / pageGroupSize); // 현재 페이지가 몇 번째에 있는지
	const startPage = (currentGroup - 1) * pageGroupSize + 1;
	const endPage = Math.min(currentGroup * pageGroupSize, totalPage);

	// 검색
	const [searchUser, setSearchUser] = useState('');
	const handleSearch = () => {
		setActivePage(1);
		request(
			CompanyService.workTimeOver,
			[
				company.id,
					{
						keyword: searchUser,
						startDate: moment(selectionRange.startDate).format('YYYY-MM-DD'),
						endDate: moment(selectionRange.endDate).format('YYYY-MM-DD'),
					}
			],
			null,
			(error) => {
				showNotification(tr('overtimeWorker'), tr('problemLoadingOvertimeWorker'), 'danger');
			}
		)
	}

	// 엑셀 다운로드
	const overtimeWorkersExcelDownload = () => {
		const xlsx = require('sheetjs-style-v2');
		const excelData = sortedData.map((v) => ({
			[tr('no')]: v.workHourRank, // 순위
			[t('사번')]: v.companyNumber,
			[t('이름')]: getPersonDisplayName(v.user),
			[t('부서')]: localizedLabel(t, v.departments?.department) || v.departmentName,
			[tr('worktime')]: convertMinsToHrsMins_v2(v.weekWorkingTotal + v.weekWorkingOverTotal), // 총 근무시간
			[tr('overWorktime')]: v.weekWorkingOverTotal !== 0 ? convertMinsToHrsMins_v2(v.weekWorkingOverTotal) : '-', // 초과시간
			[tr('overPercentage') + '(%)']: v.percent, // 초과율
			[t('상태')]: v.percent >= dangerCode?.over ? t('초과') : v.percent < warnCode?.over ? '-' : tr('warning')
		}));

		const worksheet = xlsx.utils.json_to_sheet(excelData); // 데이터를 시트로 변환

		/////// 스타일 정의 --s
        const borderStyle = {
            top: { style: 'thin', color: { rgb: 'FFD9DDEB' } },
            bottom: { style: 'thin', color: { rgb: 'FFD9DDEB' } },
            left: { style: 'thin', color: { rgb: 'FFD9DDEB' } },
            right: { style: 'thin', color: { rgb: 'FFD9DDEB' } }
        };

        const headerStyle = {
            font: {
                name: '맑은 고딕',
                bold: true,
                sz: 11,
                color: { rgb: 'FFFFFFFF' }
            },
            fill: {
                patternType: 'solid',
                fgColor: { rgb: 'FF556EE6' }
            },
            alignment: {
                horizontal: 'center',
                vertical: 'center',
                wrapText: true
            },
            border: borderStyle
        };

        const bodyStyle = {
            font: {
                name: '맑은 고딕',
                bold: false,
                sz: 10,
                color: { rgb: 'FF333333' }
            },
            alignment: {
                horizontal: 'center',
                vertical: 'center',
                wrapText: true
            },
            border: borderStyle
        };

        const zebraFillStyle = {
            patternType: 'solid',
            fgColor: { rgb: 'FFF8F9FC' }
        };

		// 컬럼 너비
		worksheet['!cols'] = [
			{ wch: 7 }, // 순위
			{ wch: 10 }, // 사번
			{ wch: 18 }, // 이름
			{ wch: 18 }, // 부서
			{ wch: 18 }, // 근로 시간
			{ wch: 18 }, // 초과 시간
			{ wch: 10 }, // 퍼센트(%)
			{ wch: 7 }, // 상태
		]

		// 행 높이 + 틀고정
		const range = xlsx.utils.decode_range(worksheet['!ref']);
		worksheet['!rows'] = Array.from({ length: range.e.r + 1 }, (_, i) => ({
			hpt: i === 0 ? 35 : 24
		}));
		worksheet['!freeze'] = { xSplit: 0, ySplit: 1 };

		// 셀 순회하며 스타일 적용
		for (let R = range.s.r; R <= range.e.r; R++) {
			for (let C = range.s.c; C <= range.e.c; C++) {
				const cell = worksheet[xlsx.utils.encode_cell({ r: R, c: C })];
				if (!cell) continue;

				if (R === 0) {
					cell.s = headerStyle;  // 헤더
				} else {
					cell.s = {
						...bodyStyle,
						fill: R % 2 === 0 ? zebraFillStyle : undefined
					};
				}
			}
		}
		/////// 스타일 정의 --e

		worksheet['!autofilter'] = { ref: 'A1:H1' } // 필터 추가할 범위
		const workbook = xlsx.utils.book_new(); // 빈 엑셀파일 생성
		xlsx.utils.book_append_sheet(workbook, worksheet, tr('overtimeWorker')); // 시트명
		xlsx.writeFile(workbook, tr('overtimeWorkerList')+'.xlsx'); // 파일명
	}

	// 초과 근무자 데이터 조회
	useEffect(() => {
		request(
			CompanyService.workTimeOver,
			[
				company.id,
					{
						keyword: searchUser,
						startDate: moment(selectionRange.startDate).format('YYYY-MM-DD'),
						endDate: moment(selectionRange.endDate).format('YYYY-MM-DD'),
					}
			],
			null,
			(error) => {
				showNotification(tr('overtimeWorker'), tr('problemLoadingOvertimeWorker'), 'danger');
			}
		);
	}, [company, request, t, selectionRange]);

	// 초과 근무자 목록 테이블 페이지 당 개수: 8행 고정 (나머지는 페이지네이션)
	useEffect(() => {
		setPerPage(8);
	}, []);

	return (
		<Card className='custom-box-shadow rounded-2 overtime_card mb-0' stretch>
			{/* 초과 근무자 현황 */}
			<CardHeader className='rounded-2 flex-row'>
				<CardLabel>
					<CardTitle className='d-flex align-items-center'>
						<span>{t('userDashboard.exceedEmployeeStatus', { num: workTime + workOverTime })}</span>
						<Badge
							className='text-wrap ms-2 px-3'
							color='danger'
							rounded='2'
							isLight={true}
							children={t('총') + ' ' + sortedData.length + t('명')}
						/>
					</CardTitle>
				</CardLabel>
				<CardActions className='d-flex'>
					<ButtonBasic
						type='button'
						color='success'
						icon='FileEarmarkExcelFill'
						style={{ fontSize: '1rem' }}
						onClick={() => {
							if(sortedData.length < 1){
								showNotification(tr('downloadExcel'), t('annualLeavePromotion.noDataAvailable'), 'danger');
							}else{
								overtimeWorkersExcelDownload();
							}
						}}
					>
						{tr('downloadExcel')}
					</ButtonBasic>
					<div
						className='d-flex align-items-center cursor-pointer'
						onClick={() => {
							navigate(adminsMenu.workingManagement.subMenu.workingLog.path, {
								state:{
									overWork: true, // 근태 콤보 필터
									startDate: moment(selectionRange.startDate).format('YYYY-MM-DD'),
									endDate: moment(selectionRange.endDate).format('YYYY-MM-DD'),
								},
							});
						}}
						style={{ color: '#03B584', cursor: 'pointer'}}
					>
						{t('common.label.seeMore')} <Icon icon='KeyboardArrowRight' size='lg' />
					</div>
				</CardActions>
			</CardHeader>
			<CardBody className='py-2' style={{flex:'unset'}}>
				<ul className='status_list'>
					<li className='bg-l10-danger'>
						<span className='tit'>{tr('exceedstatus')} ({dangerCode?.over}% {t('초과')})</span>
						<strong className='text-danger'>
							{sortedData.filter((v) =>
								v.percent >= dangerCode?.over).length
							}{t('명')}
						</strong>
						<Icon icon='ExclamationTriangle' size='2x' color='danger' className='ico' />
					</li>
					<li className='bg-l10-warning'>
						<span className='tit'>{tr('exceedstatus2')} ({warnCode?.over}% ~ {warnCode?.under}%)</span>
						<strong className='text-warning'>
							{sortedData.length - (sortedData.filter((v) =>	v.percent >= dangerCode?.over).length) - (sortedData.filter((v) =>	v.percent < warnCode?.over).length)}{t('명')}
						</strong>
						<Icon icon='ExclamationCircle' size='2x' color='warning' className='ico' />
					</li>
					<li className='bg-l10-success'>
						<span className='tit'>{tr('workTimeAvg')}</span>
						<strong className='text-success'>
							{sortedData.length > 0 ?
								convertMinsToHrsMins_v2(sortedData.reduce((acc, cur) => acc + cur.weekWorkingTotal + cur.weekWorkingOverTotal, 0) / sortedData.length)
								: convertMinsToHrsMins_v2(0) // 데이터 없을 때도 다국어 처리된 0시간 표시
							}
						</strong>
						<Icon icon='Clock' size='2x' color='success' className='ico' />
					</li>
				</ul>
			</CardBody>

			{/* 초과 근무자 목록 */}
			<CardHeader className='rounded-2 flex-row pt-2 pb-2'>
				<CardLabel>
					<CardTitle className='d-flex align-items-center' style={{fontSize:'1rem'}}>
						{/* 달력 필터 */}
						<CalendarPresetFilter
							popRef={dateFilterPopRef}
							isDisabled={isDisabled}
							matchBy='index'
							selected={selectedButton}
							onSelect={setSelectedButton}
							options={[
								{ value: 'week', label: t('common.label.weekly') },
								{ value: 'month', label: t('common.label.monthly') },
								{ value: 'quarter', label: tr2('quarterly') },
								{ value: 'half', label: tr2('halfYear') },
								{ value: 'year', label: tr2('year') },
							]}
							yearOptions={yearFilter}
							triggerClassName={null}
							triggerButtonProps={{ style: { position: 'realative' } }}
							onTriggerClick={() => {
								setDateFilterOpen(!dateFilterOpen);
							}}
							onApply={(filterValue, filterText) => {
								filterApply({ filterValue, filterKeyword: '날짜', filterText });
							}}
							onReset={() => {
								setSelectionRange({
									key: 'selection',
									startDate: moment().startOf('isoWeek').toDate(),
									endDate: moment().endOf('isoWeek').toDate(),
								});
							}}
						/>

						{/* 달력 */}
						<CalendarBox
							className='border border-light rounded bg_white ms-1'
							isDisabled={isDisabled}
							prevClassName='border-0 border-end rounded-0 border-light p-0'
							nextClassName='border-0 border-start rounded-0 border-light p-0'
							placement='auto'
							labelClassName='px-2 text-dark fw-bold'
							label={`${moment(selectionRange?.startDate).format('YYYY.MM.DD')} ~ ${moment(selectionRange?.endDate).format('YYYY.MM.DD')}`}
							popRef={popRef}
							onPrev={handlePrevClick}
							onNext={handleNextClick}
							popoverDesc={
								<div className='row'>
									<div className='col-xl-12'>
										<Card className='shadow-none w-100 mb-0'>
											<CardBody className='px-0 pb-0 row'>
												<DateRange
													locale={locales[lang]}
													editableDateInputs={true}
													dragSelectionEnabled={isDisabled}
													dateDisplayFormat='yyyy-MM-dd'
													ranges={tempSelectionRange}
													moveRangeOnFirstSelection={false}
													retainEndDateOnFirstSelection={false}
													className='d-flex'
													onChange={(item) => {
														setTempSelectionRange([item.selection]);
													}}
													months={2}
													direction='horizontal'
												/>
											</CardBody>
											<CardFooter className='py-0 my-0'>
												<CardFooterRight>
													<Button
														type='button'
														color='success'
														onClick={() => {
															handlePopoversSearchClick();
														}}
													>
														{tr2('viewResult')}
													</Button>
												</CardFooterRight>
											</CardFooter>
										</Card>
									</div>
								</div>
							}
						/>
					</CardTitle>
				</CardLabel>
				<CardActions style={{display:'flex'}}>
					<Input
						type='text'
						placeholder={tr('searchPlaceholder')}
						className='inp_search'
						value={searchUser}
						disabled={loading}
						onChange={(e) => {
							setSearchUser(e.target.value);
						}}
						onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
					/>
				</CardActions>
			</CardHeader>
			<CardBody className='pt-1'>
				{loading ?
					<DashboardLoadingSpinner />
					: sortedData.length > 0 ?
						<>
							<style>{`
								.ot_worktime_bar .time-bar-label {
									width: auto !important;
									white-space: nowrap !important;
								}
							`}</style>
							<div className='table-responsive'>
								<table className='table table-modern mb-1' style={{minWidth:'520px'}}>
									<caption className='visually_hidden'>{tr('overtimeWorker')}</caption>
									<colgroup>
										<col width='45px'/>
										<col width='70px'/>
										<col width='210px'/>
										<col width='150px'/>
										<col width='120px'/>
										<col width='70px'/>
										<col width='70px'/>
									</colgroup>
									<thead>
										<tr>
											<th scope='col'>{tr('no')}</th>
											<th scope='col'>{t('이름')}</th>
											<th scope='col'>{t('부서')}</th>
											<th scope='col'>{tr('worktime')}</th>
											<th scope='col'>{tr('overWorktime')}</th>
											<th scope='col'>{tr('overPercentage')}</th>
											<th scope='col'>{t('상태')}</th>
										</tr>
									</thead>
									<tbody>
										{sortedData.slice((activePage - 1) * perPage, activePage * perPage).map((v) => {
											const isDanger  = v.percent >= dangerCode?.over;
											const isWarning = v.percent < warnCode?.over;

											return (
												<tr key={v.id}>
													<td>{v.workHourRank}</td>
													<td>{getPersonDisplayName(v.user)}</td>
													<td>{localizedLabel(t, v.departments?.department) || v.departmentName}</td>
													<td className='worktime'>
														<div className='ot_worktime_bar'>
															{isDanger ?
																<TimeBar
																	label={convertMinsToHrsMins_v2(v.weekWorkingTotal + v.weekWorkingOverTotal)}
																	percent={100}
																	color={'#f35421'}
																	percentShow={false}
																/>
																: <TimeBar
																	label={convertMinsToHrsMins_v2(v.weekWorkingTotal + v.weekWorkingOverTotal)}
																	percent={v.percent}
																	color={'#ffcf52'}
																	percentShow={false}
																/>
															}
														</div>
													</td>
													<td className={`overtime ${isDanger ? 'text-danger fw-bold' : ''}`}>
														{convertMinsToHrsMins_v2(v.weekWorkingOverTotal)}
													</td>
													<td className={`${isDanger ? 'text-danger fw-bold' : ''}`}>{v.percent}%</td>
													<td>
														{isDanger ?
															<Badge
																color='danger'
																isLight={true}
																children={t('초과')}
															/>
															: isWarning ?
																<>-</>
																: <Badge
																	color='warning'
																	isLight={true}
																	children={tr('warning')}
																/>
														}
													</td>
												</tr>
											)
										})}
									</tbody>
								</table>
							</div>

							<Pagination className='mt-3'>
								<PaginationItem
									isFirst
									onClick={() => setActivePage(1)}
								/>
								<PaginationItem
									isPrev
									onClick={() => activePage > 1 && setActivePage(activePage - 1)}
								/>
								{Array.from({length: endPage - startPage + 1}, (_, i) => i + startPage).map((v) => (
									<PaginationItem
										key={v}
										isActive={ activePage === v }
										onClick={() => setActivePage(v)}
									>
										{v}
									</PaginationItem>
								))}
								<PaginationItem
									isNext
									onClick={() => (activePage < totalPage) && setActivePage(activePage + 1)}
								/>
								<PaginationItem
									isLast
									onClick={() => setActivePage(totalPage)}
								/>
							</Pagination>
						</>
						: <div className='no_data type_h_full'>
							<div className='img_box'><img src={imgNoData} alt='' /></div>
							<p>{t('내역이 비어있습니다')}</p>
						</div>
				}
			</CardBody>
		</Card>
	);
});

const DashboardLoadingSpinner = () => {
	return (
		<div
			className='h-100 d-flex justify-content-center align-items-center position-absolute top-50 start-50 translate-middle'>
			<Spinner className='me-1' tag='span' color='primary' isGrow/>
			<Spinner className='me-1' tag='span' color='secondary' isGrow/>
			<Spinner className='me-1' tag='span' color='dark' isGrow />
		</div>
	);
};

export default OvertimeWorkers;
